import React from 'react';
import { Box, Typography, Button, Table, TableBody, TableCell, TableHead, TableRow, Chip, useMediaQuery, useTheme } from '@mui/material';
import { X } from 'lucide-react';

const ComparisonDrawer = ({ circles, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box className="all-circles-comparison-drawer">
      {/* IMPROVED: Header with better typography */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography 
          variant="h6" 
          sx={{ 
            color: '#222222', 
            fontWeight: 700,
            fontSize: '1.25rem',
            letterSpacing: '0.2px'
          }}
        >
          Comparing {circles.length} Circles
        </Typography>
        <Button 
          onClick={onClose} 
          startIcon={<X size={18} />}
          variant="outlined"
          sx={{
            color: '#333333',
            borderColor: 'rgba(0, 0, 0, 0.3)',
            fontWeight: 600,
            '&:hover': {
              borderColor: '#333333',
              backgroundColor: 'rgba(0, 0, 0, 0.05)'
            }
          }}
        >
          Clear Comparison
        </Button>
      </Box>

      {/* IMPROVED: Table with zebra striping and better styling */}
      <Box sx={{ overflowX: 'auto' }}>
        <Table sx={{ tableLayout: isMobile ? 'auto' : 'fixed', minWidth: isMobile ? 600 : 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ 
                fontWeight: 700, 
                color: '#222222', 
                backgroundColor: '#eaeaea',
                borderBottom: '2px solid #dddddd',
                width: isMobile ? 'auto' : '20%',
                fontSize: '0.95rem',
                padding: '14px 16px',
                letterSpacing: '0.3px'
              }}>
                Feature
              </TableCell>
              {circles.map(circle => (
                <TableCell 
                  key={circle.id} 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#222222',
                    backgroundColor: '#eaeaea',
                    borderBottom: '2px solid #dddddd',
                    width: isMobile ? 'auto' : `${80/circles.length}%`,
                    fontSize: '0.95rem',
                    padding: '14px 16px',
                    letterSpacing: '0.3px'
                  }}
                >
                  {circle.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Carrier Row */}
            <TableRow sx={{ backgroundColor: '#f8f8f8' }}>
              <TableCell sx={{ 
                color: '#333333', 
                fontWeight: 600,
                padding: '12px 16px',
                fontSize: '0.9rem',
                borderBottom: '1px solid #e0e0e0'
              }}>
                Carrier
              </TableCell>
              {circles.map(circle => (
                <TableCell 
                  key={circle.id} 
                  sx={{ 
                    color: '#333333',
                    padding: '12px 16px',
                    fontSize: '0.9rem',
                    borderBottom: '1px solid #e0e0e0'
                  }}
                >
                  {circle.carrier_name}
                </TableCell>
              ))}
            </TableRow>

            {/* Plan Row */}
            <TableRow>
              <TableCell sx={{ 
                color: '#333333', 
                fontWeight: 600,
                padding: '12px 16px',
                fontSize: '0.9rem',
                borderBottom: '1px solid #e0e0e0'
              }}>
                Plan
              </TableCell>
              {circles.map(circle => (
                <TableCell 
                  key={circle.id} 
                  sx={{ 
                    color: '#333333',
                    padding: '12px 16px',
                    fontSize: '0.9rem',
                    borderBottom: '1px solid #e0e0e0'
                  }}
                >
                  {circle.plan_name}
                </TableCell>
              ))}
            </TableRow>

            {/* Price Row with right alignment */}
            <TableRow sx={{ backgroundColor: '#f8f8f8' }}>
              <TableCell sx={{ 
                color: '#333333', 
                fontWeight: 600,
                padding: '12px 16px',
                fontSize: '0.9rem',
                borderBottom: '1px solid #e0e0e0'
              }}>
                Price
              </TableCell>
              {circles.map(circle => (
                <TableCell 
                  key={circle.id} 
                  sx={{ 
                    color: '#333333',
                    padding: '12px 16px',
                    fontSize: '0.9rem',
                    borderBottom: '1px solid #e0e0e0',
                    textAlign: 'right' // Right align price
                  }}
                >
                  ${circle.monthly_payment_per_line}/month
                </TableCell>
              ))}
            </TableRow>

            {/* Features Row with improved chips */}
            <TableRow>
              <TableCell sx={{ 
                color: '#333333', 
                fontWeight: 600,
                padding: '12px 16px',
                fontSize: '0.9rem',
                borderBottom: '1px solid #e0e0e0',
                verticalAlign: 'top'
              }}>
                Features
              </TableCell>
              {circles.map(circle => (
                <TableCell 
                  key={circle.id}
                  sx={{ 
                    padding: '12px 16px',
                    borderBottom: '1px solid #e0e0e0',
                    verticalAlign: 'top'
                  }}
                >
                  <Box display="flex" flexWrap="wrap" gap={0.8}>
                    {circle.features?.filter(f => f.is_primary).map(feature => (
                      <Chip
                        key={feature.id}
                        label={feature.short_description}
                        size="small"
                        sx={{ 
                          margin: '2px',
                          backgroundColor: '#f0f0f0',
                          color: '#333333',
                          border: '1px solid #e0e0e0',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          height: '26px'
                        }}
                      />
                    ))}
                  </Box>
                </TableCell>
              ))}
            </TableRow>

            {/* Available Slots Row with right alignment */}
            <TableRow sx={{ backgroundColor: '#f8f8f8' }}>
              <TableCell sx={{ 
                color: '#333333', 
                fontWeight: 600,
                padding: '12px 16px',
                fontSize: '0.9rem',
                borderBottom: '1px solid #e0e0e0'
              }}>
                Available Slots
              </TableCell>
              {circles.map(circle => (
                <TableCell 
                  key={circle.id} 
                  sx={{ 
                    color: '#333333',
                    padding: '12px 16px',
                    fontSize: '0.9rem',
                    borderBottom: '1px solid #e0e0e0',
                    textAlign: 'right' // Right align numbers
                  }}
                >
                  {circle.available_slots}
                </TableCell>
              ))}
            </TableRow>

            {/* Rating Row with right alignment */}
            <TableRow>
              <TableCell sx={{ 
                color: '#333333', 
                fontWeight: 600,
                padding: '12px 16px',
                fontSize: '0.9rem',
                borderBottom: '1px solid #e0e0e0'
              }}>
                Rating
              </TableCell>
              {circles.map(circle => (
                <TableCell 
                  key={circle.id} 
                  sx={{ 
                    color: '#333333',
                    padding: '12px 16px',
                    fontSize: '0.9rem',
                    borderBottom: '1px solid #e0e0e0',
                    textAlign: 'right' // Right align ratings
                  }}
                >
                  {circle.rating || 4}/5
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default ComparisonDrawer;